import { useEffect, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { adminFetch } from "@/hooks/use-admin-session";
import { cn } from "@/lib/utils";

// Numeroloji'nin NumerolojiMeditationEditor.tsx içindeki "AI ile Oluştur" akışının
// (ElevenLabs + deterministik sessizlik pipeline'ı) küçültülmüş, tek-alan (ses_url)
// versiyonu. Aynı endpoint'i (/api/admin/elevenlabs/generate + status polling) çağırır,
// sadece "bucket: duygusifa-media" parametresiyle. Pipeline'ın kendisi (parseSegments,
// stripMp3Headers, generateSilenceBuffer) server tarafında değişmedi — bkz. CLAUDE.md.
const DEFAULT_VOICE_ID = "6SGlfUEeyAQoAhSmnJuV";

export function SelmaAiVoiceGenerator({ onGenerated }: { onGenerated: (url: string) => void }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const isGeneratingRef = useRef(false);
  const [elapsed, setElapsed] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [resultUrl, setResultUrl] = useState<string | null>(null);
  const [resumed, setResumed] = useState(false);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  const baseInput =
    "w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-emerald-500/30";

  function startGenerate() {
    if (isGeneratingRef.current) return;
    isGeneratingRef.current = true;
    setError(null);
    setResultUrl(null);
    setResumed(false);
    setElapsed(0);
    setLoading(true);
    timerRef.current = setInterval(() => setElapsed((s) => s + 1), 1000);

    const stopLoading = () => {
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      isGeneratingRef.current = false;
      setLoading(false);
    };

    void (async () => {
      let jobId: string;
      try {
        const res = await adminFetch("/api/admin/elevenlabs/generate", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            text,
            voiceId: DEFAULT_VOICE_ID,
            bucket: "duygusifa-media",
          }),
        });
        const json = await res.json();
        if (!res.ok) throw new Error(json.error ?? "Oluşturulamadı.");
        jobId = json.jobId;
        if (json.resumed) setResumed(true);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Bir hata oluştu.");
        stopLoading();
        return;
      }

      pollRef.current = setInterval(async () => {
        try {
          const res = await adminFetch(`/api/admin/elevenlabs/generate/status/${jobId}`);
          const json = await res.json();
          if (!res.ok || !json.ok) {
            stopLoading();
            setError(json?.error ?? "Durum sorgulanamadı.");
            return;
          }
          if (json.status === "done") {
            stopLoading();
            const url = Array.isArray(json.audioUrls) ? json.audioUrls[0] : null;
            if (url) {
              setResultUrl(url);
              onGenerated(url);
            } else {
              setError("Ses oluşturuldu ama URL alınamadı.");
            }
          } else if (json.status === "error") {
            stopLoading();
            setError(json.error ?? "Ses oluşturulamadı.");
          }
          // status === 'pending' → poll'a devam
        } catch {
          // Ağ hatası — poll'a devam et
        }
      }, 2500);
    })();
  }

  const estimatedSeconds = Math.max(15, Math.ceil(text.length / 1000) * 15);
  const pct = Math.min(100, Math.round((elapsed / estimatedSeconds) * 100));
  const remaining = Math.max(0, estimatedSeconds - elapsed);

  return (
    <div>
      <Button type="button" variant="outline" size="sm" className="rounded-full" onClick={() => setOpen((o) => !o)}>
        {open ? "AI Panelini Kapat" : "AI ile Oluştur"}
      </Button>
      {open && (
        <div className="mt-3 space-y-3 rounded-xl border border-emerald-200 bg-emerald-50/50 p-4">
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Meditasyon Metni</label>
            <textarea
              className={cn(baseInput, "min-h-[140px] resize-y")}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder='Seslendirilecek metni girin. Duraklama eklemek için: "[Duraklama 3 saniye]"'
              disabled={loading}
            />
            <div className="mt-1 text-[11px] text-slate-400">
              Metin içinde <code>[Duraklama X saniye]</code> işareti kullanarak sessizlik ekleyebilirsiniz. Örn: "Nefes al. [Duraklama 3 saniye] Nefes ver."
            </div>
          </div>

          {error && (
            <div className="rounded-xl border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">{error}</div>
          )}

          {resultUrl && (
            <div className="space-y-2 rounded-xl border border-green-500/30 bg-green-500/10 p-3">
              <div className="text-sm font-semibold text-green-800">Ses oluşturuldu, alan otomatik dolduruldu ✓</div>
              <audio controls src={resultUrl} className="w-full" />
            </div>
          )}

          <Button type="button" size="sm" className="rounded-full" disabled={!text.trim() || loading} onClick={startGenerate}>
            {loading ? "Oluşturuluyor..." : "Oluştur"}
          </Button>

          {loading && (
            <div className="flex flex-col gap-2">
              {resumed && (
                <div className="text-xs text-blue-600">Devam eden bir üretim bulundu — sonuç bekleniyor...</div>
              )}
              <div className="flex justify-between text-xs text-slate-500">
                <span className="animate-pulse">Ses oluşturuluyor...</span>
                <span>{elapsed}s / ~{estimatedSeconds}s ({remaining > 0 ? `~${remaining}s kaldı` : "bitiyor..."})</span>
              </div>
              <div className="h-2 w-full overflow-hidden rounded-full bg-slate-200">
                <div className="h-full rounded-full bg-emerald-500 transition-all duration-1000" style={{ width: `${pct}%` }} />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
